import { inject, Injectable, NgZone } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { Row } from 'src/app/models/ui/table';
import { first, firstValueFrom, map, tap } from 'rxjs';
import { APIService } from '../APIService/api.service';
import { AppDB } from './dexieDB';
import { DexieError, IndexableType } from 'dexie';
import { ProgressHandler } from 'src/app/types/progress';
import { HttpEventType } from '@angular/common/http';
import { errorHandle, formatBytes } from 'src/app/misc/functions';

export type SyncBackMode = 'withoutChanges' | 'withChanges';

export interface SetupConfig {
  IDs: number[];
  repType?: 'Inspektion' | 'Wartung' | 'Begehung';
  userId?: number;
  repId?: number;
}

const TABLES_SYNC_DELETE = ['ospez', 'szumz', 'opbild', 'sxbep', 'obauw', 'oherst', 'sbfek'] as const;

@Injectable({
  providedIn: 'root',
})
export class ReplikatSetupService {
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastrService);
  private readonly api = inject(APIService);
  private readonly zone = inject(NgZone);
  private readonly db = inject(AppDB);

  constructor() {
    if (this.api.isRep) {
      this.patchDexieToRunOutsideZone();
      this.patchDexieToSyncMetaData();
    }
  }

  //#region setup
  async replicate(config: SetupConfig, connectionId: string, progressHandler: ProgressHandler) {
    await this.zone.runOutsideAngular(async () => {
      let repIdToClearOnError: number;
      try {
        // if (config.IDs.length > 40){
        //   this.toastr.error(
        //     `
        //       ${this.mrTranslate.transform('Die maximalen Anzahl von Anlagen für das Replikat wurde überschritten.')}
        //       <br/><br/>
        //       ${this.mrTranslate.transform('Es sind maximal 40 Objkte zulässig.')}
        //     `,
        //     this.mrTranslate.transform('Anzahl überschritten'),
        //     { enableHtml: true }
        //   );
        //   progressHandler?.taskErrorer({ message: '>40 Objekte' });
        //   return;
        // }
        console.time('Total');
        console.time('from Back-End');

        let content_length = 0;
        const { success, error, repID, tables, userID } = await firstValueFrom(
          this.api.setupReplicate(config, connectionId).pipe(
            tap(event => {
              switch (event.type) {
                case HttpEventType.Sent: {
                  progressHandler?.taskStarter({ id: 'from-server', title: 'Tabellen von Server laden' });
                  break;
                }
                case HttpEventType.ResponseHeader: {
                  content_length = Number(event.headers.get('content-length'));
                  break;
                }
                case HttpEventType.DownloadProgress: {
                  const total = event.total || content_length;
                  progressHandler?.taskProgressChanger({
                    id: 'from-server',
                    percent: total ? Math.floor((event.loaded * 100) / total) : null,
                    current: !total ? formatBytes(event.loaded) : null,
                  });
                  break;
                }
                case HttpEventType.Response: {
                  progressHandler?.taskEnder({ id: 'from-server' });
                  break;
                }
              }
            }),
            first(event => event.type == HttpEventType.Response),
            map(event => event.body)
          )
        );

        console.timeEnd('from Back-End');

        if (!success) {
          this.toastr.error(error, this.mrTranslate.transform('Etwas ist schief gelaufen'), { disableTimeOut: true });
          progressHandler?.taskErrorer({ message: error });
          return;
        }

        //* Do not delete Replikat on Error, if called from login
        if (!config.repId) repIdToClearOnError = repID;

        if (tables) {
          localStorage.setItem('repID', repID.toString());
          localStorage.setItem('repDB', localStorage.getItem('intdb') || localStorage.getItem('DB'));
          await this.setReplicateTables(tables, userID, progressHandler);
          this.toastr.success(this.mrTranslate.transform('Replikat erfolgreich erstellt.'));

          this.toastr.info(this.mrTranslate.transform('App wird neu geladen'));
          setTimeout(() => window.location.reload(), 1000);
        } else {
          this.toastr.success(
            this.mrTranslate.transform('Replikat wird durchgeführt, wenn entsprechender Benuzter sich angemeldet hat.')
          );
        }
      } catch (er) {
        console.timeEnd('from Back-End');
        console.timeEnd('delete IndexedDB');
        console.timeEnd('Tables total');
        console.timeEnd('Total');

        progressHandler?.taskErrorer({ message: (er as Error).message });
        console.error('ERROR startReplicate', er);
        if (repIdToClearOnError) this.stopReplicate('withoutChanges', '-', repIdToClearOnError);
      }
    });
  }

  private async setReplicateTables(
    tables: Record<string, { list: Row[]; maxID: number }>,
    userID: number,
    progressHandler?: ProgressHandler
  ) {
    console.time('delete IndexedDB');
    await this.db.delete({ disableAutoOpen: false });
    console.timeEnd('delete IndexedDB');

    console.time('Tables total');
    progressHandler?.taskStarter({ id: 'populate-tables', title: 'Tabellen lokal erstellen' });

    await this.db.transaction('rw', this.db.tables, async () => {
      await this.db.user.add({ ID: userID });

      const tablesMap = Object.entries(tables);
      for (let i = 0; i < tablesMap.length; i++) {
        const [tableName, { list, maxID }] = tablesMap[i];
        try {
          console.time(`Table ${tableName}`);

          const table = this.db.table(tableName);
          const lastID = (await table.bulkAdd(list)) ?? 0;

          if (maxID && maxID != lastID) {
            try {
              await table.add({ ID: maxID });
              await table.delete(maxID);
            } catch (ex) {
              if ((ex as DexieError).name !== 'ConstraintError') throw ex;
            }
          }
          if (tableName != 'media') this.db.meta.add({ tableName, deletedItems: [], maxID: maxID || lastID });

          progressHandler?.taskProgressChanger({
            id: 'populate-tables',
            percent: Math.floor(((i + 1) * 100) / tablesMap.length),
          });
        } catch (_) {
          throw _;
        } finally {
          console.timeEnd(`Table ${tableName}`);
        }
      }
      progressHandler?.taskEnder({ id: 'populate-tables' });
    });
    console.timeEnd('Tables total');
    console.timeEnd('Total');
  }

  //#endregion
  //#region Syncback
  async checkIfOnline(): Promise<boolean> {
    try {
      await firstValueFrom(this.api.requestHead('replikat/ping'));
      return true;
    } catch (er) {
      console.error('checkIfOnline: ', errorHandle(er));
      this.toastr.error(this.mrTranslate.transform('Sie müssen Online sein um das Replikat zurückspielen zu können'));
      return false;
    }
  }

  async stopReplicate(
    syncWithoutChanges: SyncBackMode,
    connectionId = '-',
    repID?: number,
    progressHandler?: ProgressHandler
  ) {
    await this.zone.runOutsideAngular(async () => {
      try {
        console.time('Total');
        //Transferiere die Tables wieder in ein Array mit Tables, die einen Namen und Key-Value-Reihen haben, so wie wir es lieben.
        console.time('Tables');
        const tables = syncWithoutChanges == 'withChanges' ? await this.getAllTables(progressHandler) : null;
        console.timeEnd('Tables');
        progressHandler?.taskStarter({ id: 'to-server', title: 'Tabellen auf Server hochladen' });

        const { success, error } = await firstValueFrom(
          this.api.syncbackReplicate(connectionId, { tables, repID: repID ?? +localStorage.getItem('repID') }).pipe(
            tap(event => {
              if (event.type != HttpEventType.UploadProgress) return;

              const { loaded, total } = event;
              progressHandler?.taskProgressChanger({
                id: 'to-server',
                current: formatBytes(loaded),
                // percent: total ? Math.floor(loaded * 100 / total) : null,
                // current: !total ? formatBytes(loaded) : null
              });
              if (loaded >= total) progressHandler?.taskEnder({ id: 'to-server' });
            }),
            first(event => event.type == HttpEventType.Response),
            map(event => event.body)
          )
        );

        if (success) {
          // localStorage.removeItem('DB');
          localStorage.removeItem('repID');
          localStorage.removeItem('repDB');

          console.time('Delete');
          progressHandler?.taskStarter({ id: 'delete-db', title: 'lokales Replikat freigeben' });

          await this.db.delete({ disableAutoOpen: false });

          progressHandler?.taskProgressChanger({ id: 'delete-db', percent: 100 });
          progressHandler?.taskEnder({ id: 'delete-db' });
          console.timeEnd('Delete');

          if (repID) return;

          this.toastr.success(this.mrTranslate.transform('Replikat erfolgreich zurückgespielt.'));
          this.toastr.info(this.mrTranslate.transform('App wird neu geladen'));

          setTimeout(() => window.location.reload(), 1000);
        } else {
          this.toastr.error(error, this.mrTranslate.transform('Fehler beim zurückspielen des Replikats'));
          progressHandler?.taskErrorer({ message: error });
        }
      } catch (er) {
        console.timeEnd('Tables');
        console.error('ERROR stopReplicate: ', er);
        progressHandler?.taskErrorer({ message: (er as Error).message });
        this.toastr.error(
          this.mrTranslate.transform((er as Error).message),
          this.mrTranslate.transform('Fehler beim zurückspielen des Replikats')
        );
      } finally {
        console.timeEnd('Total');
      }
    });
  }

  private async getAllTables(progressHandler: ProgressHandler) {
    const tables_count = this.db.tables.length;
    progressHandler?.taskStarter({ id: 'tables-db', title: 'Tabellen vor dem Senden vorbereiten' });

    const tableEntries = await Promise.all(
      this.db.tables.map<Promise<[string, Row[]]>>(async (table, i) => {
        const rows = await table.toArray();
        progressHandler?.taskProgressChanger({
          id: 'tables-db',
          percent: Math.floor(((i + 1) * 100) / tables_count),
        });
        return [table.name, rows];
      })
    );
    const tableMap = Object.fromEntries(tableEntries);

    progressHandler?.taskEnder({ id: 'tables-db' });
    return tableMap;
  }
  //#endregion

  //#region Patch Modifications
  private async patchDexieToRunOutsideZone() {
    const zone = this.zone;
    this.db.use({
      stack: 'dbcore',
      name: 'outside-zone-plugin',
      create(downlevelDatabase) {
        return {
          ...downlevelDatabase,
          table(tableName: string) {
            const downlevelTable = downlevelDatabase.table(tableName);

            return {
              ...downlevelTable,
              query(req) {
                return zone.runOutsideAngular(() => downlevelTable.query(req));
              },
              mutate(req) {
                return zone.runOutsideAngular(() => downlevelTable.mutate(req));
              },
              openCursor(req) {
                return zone.runOutsideAngular(() => downlevelTable.openCursor(req));
              },
              get(req) {
                return zone.runOutsideAngular(() => downlevelTable.get(req));
              },
              getMany(req) {
                return zone.runOutsideAngular(() => downlevelTable.getMany(req));
              },
              count(req) {
                return zone.runOutsideAngular(() => downlevelTable.count(req));
              },
            };
          },
        };
      },
    });
  }

  private patchDexieToSyncMetaData() {
    this.db.use({
      stack: 'dbcore',
      name: 'sync-meta-data',
      create: _DB => ({
        ..._DB,
        transaction(stores, mode, options) {
          if (mode == 'readwrite' && !stores.includes('meta')) stores = [...stores, 'meta'];
          return _DB.transaction(stores, mode, options);
        },
      }),
    });

    TABLES_SYNC_DELETE.forEach(tableName => {
      this.db.table(tableName).hook('deleting', async function (ID, _, transaction) {
        const metaTable = transaction.table('meta');
        const meta = await metaTable.get(tableName);
        if (meta.maxID && ID > meta.maxID) return;
        meta.deletedItems.push(ID);
        return await metaTable.put({ ...meta });
      });
    });
  }
  //#endregion
}

export interface ReplikatSetup {
  repID: number;
  userID?: number;
  tables: Record<string, { list: Row[]; maxID: number }>;
}

export interface ReplikatSyncBack {
  repID: number;
  tables: Record<string, Row[]>;
}

export interface ReplikatMeta {
  tableName: string;
  deletedItems: IndexableType[];
  maxID: IndexableType;
}
